import moment from 'moment-timezone';
import { createSelector } from '@reduxjs/toolkit';
import { Cluster, ClusterId, selectClustersById } from '../slices/clusters';
import { Crop, selectCropsById } from '../slices/crops';
import {
  Parcel,
  ParcelId,
  selectClusterParcelsByParcelId,
  selectImportedParcels,
  selectLastFetchedParcel,
  selectParcel,
  selectParcelsById,
  selectParcelsIds,
} from '../slices/parcels';
import { selectSearchFilter, selectSelectedClusters } from '../slices/ui';
import { clusterContains, cropContains, parcelTypeContains } from '../utils';
import { parcelContains } from '../utils/parcels';

type ParcelWithRelations = Parcel & {
  cluster?: Cluster;
  crop?: Crop;
  current_tenant_cluster?: Cluster;
};

type VisibleParcel = ParcelWithRelations & { hidden: boolean };

function withRelations(
  parcel: Parcel,
  clusterById: Record<ClusterId, Cluster>,
  cropById: ReturnType<typeof selectCropsById>,
): ParcelWithRelations {
  return {
    ...parcel,
    cluster: clusterById[parcel.cluster_id],
    crop: parcel.current_crop_id ? cropById[parcel.current_crop_id] : undefined,
    current_tenant_cluster: clusterById[parcel.current_tenant_cluster_id],
  };
}

export const selectCurrentParcel = createSelector(
  [selectLastFetchedParcel, selectClustersById, selectCropsById],
  (parcel, clusterById, cropById) => (parcel ? withRelations(parcel, clusterById, cropById) : null),
);

export const selectParcelsAsArray = createSelector([selectParcelsIds, selectParcelsById], (parcelsIds, parcelById) =>
  parcelsIds.map((id) => parcelById[id]).filter((parcel) => !!parcel),
);

export const selectVisibleParcels = createSelector(
  [selectSelectedClusters, selectSearchFilter, selectParcelsAsArray, selectClustersById, selectCropsById],
  (activeClusters, filter, parcels, clusterById, cropById) =>
    parcels
      .filter(
        (p) =>
          (!activeClusters?.length ||
            activeClusters.includes(p.cluster_id) ||
            (p.current_tenant_cluster_id && activeClusters.includes(p.current_tenant_cluster_id)) ||
            (p.owner_cluster_id && activeClusters.includes(p.owner_cluster_id))) &&
          p.cluster_id,
      )
      .map((parcel): VisibleParcel => {
        const crop = parcel.current_crop_id ? cropById[parcel.current_crop_id] : undefined;
        return {
          ...withRelations(parcel, clusterById, cropById),
          hidden:
            !parcelContains(parcel, filter) &&
            !clusterContains(clusterById[parcel.current_tenant_cluster_id], filter) &&
            !clusterContains(parcel.owner_cluster_id ? clusterById[parcel.owner_cluster_id] : undefined, filter) &&
            !parcelTypeContains(parcel, filter) &&
            !cropContains(crop, filter),
        };
      }),
);

export const selectVisibleParcelsAreaBounds = createSelector([selectVisibleParcels], (parcels) => {
  const hectares = parcels.map((p) => Math.floor(p.area / 10000));
  return hectares.length
    ? { min: Math.max(Math.min(...hectares) - 1, 0), max: Math.max(...hectares) + 1 }
    : { min: 0, max: 1 };
});

export const selectAllParcels = createSelector([selectParcelsAsArray, selectClustersById], (parcels, clusterById) =>
  parcels
    .filter((p) => p.cluster_id)
    .map((parcel) => ({
      ...parcel,
      cluster: clusterById[parcel.cluster_id],
      current_tenant_cluster: clusterById[parcel.current_tenant_cluster_id],
    })),
);

export const selectAllParcelsWithoutInactives = createSelector([selectAllParcels], (parcels) =>
  parcels.filter((parcel) => parcel.status !== 'INACTIVE'),
);

export const selectAllVisibleParcelsWithoutInactives = createSelector([selectVisibleParcels], (parcels) =>
  parcels.filter((parcel) => parcel.status !== 'INACTIVE'),
);

export const selectImportedParcelsWithCluster = createSelector(
  [selectImportedParcels, selectClustersById],
  (importedParcels, clusterById) => ({
    ...importedParcels,
    success: (importedParcels.success || []).map((parcel) => ({
      ...parcel,
      cluster: clusterById[parcel.cluster_id],
      current_tenant_cluster: clusterById[parcel.current_tenant_cluster_id],
    })),
    error: [],
  }),
);

export const selectParcelData = createSelector(
  [selectParcel, selectClustersById, selectCropsById],
  (parcel, clusterById, cropById) => (parcel ? withRelations(parcel, clusterById, cropById) : null),
);

export const selectParcelHistoryData = createSelector(
  [selectClusterParcelsByParcelId, (_state: unknown, parcelId: ParcelId) => parcelId, selectClustersById],
  (clusterParcelsByParcelId, parcelId, clusterById) =>
    (clusterParcelsByParcelId[parcelId] || [])
      .map((clusterParcel) => ({
        ...clusterParcel,
        cluster: clusterById[clusterParcel.clusterId],
      }))
      .toSorted((a, b) => {
        if (!a.fromDate) return 1;
        if (!b.fromDate) return -1;
        return moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1;
      }),
);
